// OfflineContext — wires the OfflineQueue into the studio's React tree.
//
// Exposes:
//   • online state (navigator.onLine + listener)
//   • pending count from the queue
//   • submit_mark_paid / submit_cashbook_entry helpers that try online first
//     and fall back to the queue on network failure
//   • drain() that we auto-invoke whenever the device comes back online

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { orpc } from '../lib/orpc.js';
import {
  IdbQueueAdapter,
  InMemoryQueueAdapter,
  OfflineQueue,
  type QueueAdapter,
} from './queue.js';

function build_adapter(): QueueAdapter {
  if (typeof indexedDB !== 'undefined') return new IdbQueueAdapter();
  return new InMemoryQueueAdapter();
}

interface MarkPaidInput {
  subscription_id: string;
  payment: {
    amount_paise: number;
    method: 'upi' | 'cash' | 'cheque' | 'neft' | 'bank_transfer';
    reference?: string;
    geo?: { lat: number; lon: number };
    note?: string;
    screenshot_key?: string;
  };
}

interface CashbookInput {
  entry_date: string;
  kind:
    | 'opening_balance'
    | 'cash_in_payment'
    | 'cash_out_payout'
    | 'bank_deposit'
    | 'bank_withdrawal'
    | 'expense'
    | 'adjustment';
  amount_paise: number;
  note?: string;
}

export type SubmitResult =
  | { status: 'sent' }
  | { status: 'queued'; queued_id: string };

interface OfflineState {
  online: boolean;
  pending: number;
  syncing: boolean;
  submit_mark_paid: (input: MarkPaidInput) => Promise<SubmitResult>;
  submit_cashbook_entry: (input: CashbookInput) => Promise<SubmitResult>;
  drain: () => Promise<void>;
}

const ctx = createContext<OfflineState | null>(null);

function is_network_error(err: unknown): boolean {
  if (err instanceof TypeError) return true; // fetch throws TypeError on network failure
  const msg = err instanceof Error ? err.message.toLowerCase() : '';
  return msg.includes('network') || msg.includes('fetch') || msg.includes('failed');
}

export function OfflineProvider({ children }: { children: ReactNode }): ReactElement {
  const queue_ref = useRef<OfflineQueue | null>(null);
  if (!queue_ref.current) {
    queue_ref.current = new OfflineQueue(build_adapter(), {
      on_change: () => set_pending_tick((n) => n + 1),
    });
  }
  const queue = queue_ref.current;

  const [online, set_online] = useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const [pending_tick, set_pending_tick] = useState(0);
  const [pending, set_pending] = useState(0);
  const [syncing, set_syncing] = useState(false);

  // Recount pending whenever the queue notifies a change
  useEffect(() => {
    let cancelled = false;
    void queue.list().then((items) => {
      if (!cancelled) set_pending(items.length);
    });
    return () => {
      cancelled = true;
    };
  }, [pending_tick, queue]);

  const drain = useCallback(async (): Promise<void> => {
    if (syncing) return;
    set_syncing(true);
    try {
      await queue.drain({
        ledger_mark_paid: (input) => orpc.ledger.mark_paid(input as never),
        cashbook_create_entry: (input) => orpc.cashbook.create_entry(input as never),
      });
    } finally {
      set_syncing(false);
    }
  }, [queue, syncing]);

  // Listen to browser online/offline events
  useEffect(() => {
    const on_online = (): void => {
      set_online(true);
      void drain();
    };
    const on_offline = (): void => set_online(false);
    window.addEventListener('online', on_online);
    window.addEventListener('offline', on_offline);
    return () => {
      window.removeEventListener('online', on_online);
      window.removeEventListener('offline', on_offline);
    };
  }, [drain]);

  const submit_mark_paid = useCallback(
    async (input: MarkPaidInput): Promise<SubmitResult> => {
      const try_online = async (): Promise<SubmitResult> => {
        await orpc.ledger.mark_paid(input as never);
        return { status: 'sent' };
      };
      if (online) {
        try {
          return await try_online();
        } catch (err) {
          if (!is_network_error(err)) throw err;
          // fall through to queue
        }
      }
      const queued = await queue.enqueue('ledger.mark_paid', input);
      return { status: 'queued', queued_id: queued.id };
    },
    [online, queue],
  );

  const submit_cashbook_entry = useCallback(
    async (input: CashbookInput): Promise<SubmitResult> => {
      if (online) {
        try {
          await orpc.cashbook.create_entry(input as never);
          return { status: 'sent' };
        } catch (err) {
          if (!is_network_error(err)) throw err;
        }
      }
      const queued = await queue.enqueue('cashbook.create_entry', input);
      return { status: 'queued', queued_id: queued.id };
    },
    [online, queue],
  );

  const value: OfflineState = {
    online,
    pending,
    syncing,
    submit_mark_paid,
    submit_cashbook_entry,
    drain,
  };
  return <ctx.Provider value={value}>{children}</ctx.Provider>;
}

export function useOffline(): OfflineState {
  const v = useContext(ctx);
  if (!v) throw new Error('useOffline must be used inside <OfflineProvider>');
  return v;
}
